import { useCallback, useMemo } from 'react'

/**
 * Custom hook to memoize second order functions.
 *
 * The only rule is that the first argument of the creator must be an unique identifier.
 *
 * E.g.
 *
 *  const { create: createHandleDelete } = useSecondOrderCallback(
 *    (event, id) => dispatch(onDeleteItem({ id }))
 *  )
 *  const { create: createHandleEdit } = useSecondOrderCallback(
 *    (event, id) => onOpenEditForm({ id })
 *  )
 *
 *  <div className="root">
 *    {items.map((item, index) => (
 *      <div className="item">
 *        <div className="title">{item.title}</div>
 *        <Button onClick={createHandleEdit(item.id)}>Edit</Button>
 *        <Button onClick={createHandleDelete(item.id)}>Edit</Button>
 *      </div>
 *    )}
 *  </div>
 */
export const useSecondOrderCallback = (callback = () => undefined, dependencies = []) => {
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const callbacksDictionary = useMemo(() => ({}), dependencies)
  const createCallback = useCallback(
    (id, ...higherArgs) => {
      if (!callbacksDictionary[id]) {
        // eslint-disable-next-line n/no-callback-literal
        callbacksDictionary[id] = (...args) => callback(...args, id, ...higherArgs)
      }

      return callbacksDictionary[id]
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [callbacksDictionary, ...dependencies]
  )

  return createCallback
}
